import React, { useState } from 'react'
import type { PolicyArtefactType, PolicyItem } from '@/lib/apiTypes'
import { forcePolicyInheritance } from '@/lib/hooks'
import { RegoEditorField } from '@/features/policies/components/RegoEditor'
import { newPolicyScaffold, type Template } from '@/features/policies/lib/regoTemplates'
import { applyTemplatePick, NO_AUTOFILL, type AutofilledValues } from '@/features/policies/lib/templateAutofill'
import { mentionsDeny, type RegoSyntaxError } from '@/features/policies/lib/regoSyntax'
import { api } from '@/lib/api'
import { Modal } from '@/shared/components/Modal'
import { SCOPE_PLURAL } from '@/features/policies/components/ComplianceTooltip'

export interface PolicyModalProps {
  orgId: string
  policy?: PolicyItem
  /** Artefact the new policy is scoped to (create only; ignored when editing). */
  artefactType?: PolicyArtefactType
  /** When set (create only), the new policy is identity-custom, attached to this artefact identity
   *  (module / workspace / provider / plan key). Absent ⇒ org-baseline policy. */
  instanceKey?: string
  /** A template picked BEFORE this dialog opened (the Policies overview opens the gallery first),
   *  seeding the same three fields an in-dialog pick would. Create only. */
  template?: Template
  onClose: () => void
  onSaved: (rescan?: string) => void
}

export function PolicyModal({ orgId, policy, artefactType, instanceKey, template, onClose, onSaved }: PolicyModalProps) {
  // A template chosen outside the dialog goes through the same pure helper as an in-dialog pick,
  // against an empty form, so it is remembered as autofilled and a later pick can replace its text.
  const seeded = template ? applyTemplatePick({ name: '', description: '', autofilled: NO_AUTOFILL }, template) : null
  const [name, setName]             = useState(policy?.name ?? seeded?.name ?? '')
  const [description, setDescription] = useState(policy?.description ?? seeded?.description ?? '')
  // A new policy opens on the getting-started scaffold rather than an empty box; an existing one
  // opens on its own Rego, untouched.
  const [rego, setRego]             = useState(policy?.rego ?? template?.rego ?? newPolicyScaffold(artefactType ?? 'module'))
  const [enforcing, setEnforcing]   = useState(policy?.is_enforcing !== 0)
  const [busy, setBusy]             = useState(false)
  const [apiError, setApiError]     = useState<string | null>(null)
  const [forcing, setForcing]       = useState(false)
  // The editor's syntax verdict. Nothing server-side parses Rego on write, so a policy that
  // cannot parse would be stored, bundled, and only fail at evaluation time - where the
  // author is no longer watching. Saving waits for the box to be happy.
  const [regoErrors, setRegoErrors] = useState<RegoSyntaxError[]>([])
  // What the last template pick wrote into name/description, so a second pick can replace its
  // own text while anything the author typed - and the name of a policy being edited - is left
  // alone. See lib/templateAutofill.ts.
  const [autofilled, setAutofilled] = useState<AutofilledValues>(seeded?.autofilled ?? NO_AUTOFILL)

  /** A template was picked: its Rego arrives through the editor's own onChange, and the name and
   *  description follow only into fields nobody else has claimed. */
  function handleTemplatePick(template: Template) {
    const next = applyTemplatePick({ name, description, autofilled }, template)
    setName(next.name)
    setDescription(next.description)
    setAutofilled(next.autofilled)
  }

  // Compliance rollup comes with the policy list row (edit mode only).
  const stats = policy && policy.eval_total !== undefined
    ? { artefact_type: policy.artefact_type, failing: policy.eval_failing ?? 0, total: policy.eval_total }
    : null

  // Force every instance to inherit the chosen level (resets per-instance overrides).
  async function force(value: 'advisory' | 'enforcing') {
    if (!policy) return
    setForcing(true)
    setApiError(null)
    try { await forcePolicyInheritance(orgId, policy.id, value); onSaved() }
    catch (err) { setApiError((err as Error).message) }
    finally { setForcing(false) }
  }

  // The editor opens on a commented scaffold, so "not empty" no longer means "says anything".
  // A policy with no deny rule parses, saves, and then passes everything it is asked about.
  const noDenyRule = rego.trim().length > 0 && !mentionsDeny(rego)

  const blocker = regoErrors.length > 0
    ? `Fix the Rego syntax error on line ${regoErrors[0].line} first`
    : (noDenyRule
      ? 'This policy defines no deny rule, so it could never fail anything. Uncomment the example in the editor, or start from a template.'
      : undefined)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || !rego.trim() || regoErrors.length > 0 || noDenyRule) return
    setBusy(true)
    setApiError(null)
    try {
      let rescan: string | undefined
      if (policy) {
        const res = await api.patch<{ rescan?: string }>(`/api/orgs/${orgId}/policies/${policy.id}`, { name, description, rego, is_enforcing: enforcing })
        rescan = res.rescan
      } else {
        // Identity-custom create sends both instance_key AND an explicit artefact_type (the server
        // requires it so a custom policy is never misfiled under the default tab).
        await api.post(`/api/orgs/${orgId}/policies`, {
          name, description, rego, is_enforcing: enforcing,
          ...(artefactType ? { artefact_type: artefactType } : {}),
          ...(instanceKey ? { instance_key: instanceKey, artefact_type: artefactType } : {}),
        })
      }
      onSaved(rescan)
    } catch (err) {
      setApiError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  // A custom policy is either being edited (policy.instance_key set) or created (instanceKey prop).
  const isCustom = !!(policy?.instance_key ?? instanceKey)
  const title = policy
    ? (isCustom ? 'Edit custom policy' : 'Edit policy')
    : (isCustom ? 'New custom policy' : 'New policy')

  return (
    // The Rego editor is the point of this dialog, so the panel gets a code editor's worth of
    // room: wide, full height, and the editor absorbing whatever the other fields don't use.
    <Modal track="org_policy_edit" title={title} onClose={onClose} maxWidth={1040} fillHeight>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0 }}>
        {policy && stats && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', fontSize: '0.76rem' }}>
            <span className={stats.failing > 0 ? 'badge badge-red' : 'badge badge-green'} style={{ fontSize: '0.62rem' }}>
              {stats.failing > 0 ? `${stats.failing} non-compliant` : 'compliant'}
            </span>
            <span style={{ color: 'var(--text2)' }}>
              {stats.total === 0
                ? `No ${SCOPE_PLURAL[stats.artefact_type]} evaluated against this policy yet.`
                : `${stats.failing} of ${stats.total} evaluated ${SCOPE_PLURAL[stats.artefact_type]} fail this policy.`}
            </span>
          </div>
        )}
        {policy && !isCustom && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.72rem', color: 'var(--text3)', flexWrap: 'wrap' }}>
            <span>Force all {SCOPE_PLURAL[policy.artefact_type]} to inherit:</span>
            <button type="button" className="btn btn-ghost btn-sm" disabled={forcing} style={{ fontSize: '0.68rem' }} onClick={() => force('advisory')}>Advisory</button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={forcing} style={{ fontSize: '0.68rem' }} onClick={() => force('enforcing')}>Enforcing</button>
            {forcing && <span className="spinner" />}
          </div>
        )}
        {/* Side by side while the panel is wide enough for both - every row of chrome saved here
            is a row the editor gets back on a short screen - and stacked when it isn't (auto-fit
            rather than a media query, because what matters is the panel's width, not the page's). */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, flexShrink: 0 }}>
          <div>
            <label className="field-label">Name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="No Critical Findings" />
          </div>
          <div>
            <label className="field-label">Description (optional)</label>
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Block modules with critical security findings" />
          </div>
        </div>
        <RegoEditorField
          value={rego}
          onChange={setRego}
          artefactType={policy?.artefact_type ?? artefactType ?? 'module'}
          onErrorsChange={setRegoErrors}
          onTemplatePick={handleTemplatePick}
          fill
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text2)' }}>
            <input type="checkbox" checked={enforcing} onChange={(e) => setEnforcing(e.target.checked)} />
            Enforcing
          </label>
          <span style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>
            {enforcing ? 'Failures will block downloads' : 'Failures shown as warnings only (advisory)'}
          </span>
        </div>
        {apiError && <div className="feedback-error">{apiError}</div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0 }}>
          {/* Said out loud, not just in a tooltip on a disabled button: EVERY new policy starts in
              this state, so a silent refusal would be the first thing an author meets. A syntax
              error gets no line here - the editor already paints a red bar listing them. */}
          {noDenyRule && (
            <span style={{ marginRight: 'auto', fontSize: '0.72rem', color: 'var(--text3)', lineHeight: 1.4 }}>
              No <code style={{ color: 'var(--accent)' }}>deny</code> rule yet, so this policy could never fail anything.
            </span>
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={busy || regoErrors.length > 0 || noDenyRule}
            title={blocker}
          >
            {busy ? <span className="spinner" /> : (policy ? 'Save changes' : 'Create policy')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
